import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Operation } from 'fast-json-patch'
import { ModerationStatus } from '@roadfootage/api-client'
import type {
  RecordedCrashDto,
  StaffFileProcessingResponse,
  StaffIncidentReportRelationDto
} from '@roadfootage/api-client'
import { useRestData } from '../../composables/useRestData'
import { useApi } from '../../composables/api'
import type { RelationReview } from '../../utils/relation-review'

export const useModerationStore = defineStore('moderation', () => {
  const {
    adminIncidentReportsApi,
    adminIncidentReportRelationsApi,
    adminIncidentFootageApi,
    adminIncidentImagesApi,
    adminUserFilesApi,
    crashesApi
  } = useApi()

  const queueGrid = useRestData<any>(
    (p) => adminIncidentReportsApi.adminIncidentReportsPaginate(p),
    {
      initialPageSize: 50,
      defaultSort: [{ sortBy: 'createdAt', descending: false }]
    }
  )

  queueGrid.activeFilters.value['moderationStatus'] = { v: 'Pending', op: 'eq' }

  // Pipeline step and percent per file id, for the incident open in the review dialog.
  const processingByFileId = ref<Record<string, StaffFileProcessingResponse>>({})

  // The recorded crash an incident under review is linked to (RF-070); null while loading or when it could not be loaded.
  const linkedCrash = ref<RecordedCrashDto | null>(null)

  // Relations (same incident as another report) of the incident under review (RF-133).
  const relations = ref<StaffIncidentReportRelationDto[]>([])

  const activeIncidentId = ref<string | null>(null)
  const isDialogOpen = ref(false)
  const lockedIncident = ref<any>(null)

  const activeIncident = computed(
    () =>
      queueGrid.items.value.find((i: any) => i.id === activeIncidentId.value) ??
      lockedIncident.value
  )

  const activeIndex = computed(() =>
    queueGrid.items.value.findIndex((i: any) => i.id === activeIncidentId.value)
  )

  // Flat computed properties for storeToRefs compatibility in components
  const queueTotal = computed(() => queueGrid.pagination.value.rowsNumber)
  const queueItems = computed(() => queueGrid.items.value as any[])
  const queueLoading = computed(() => queueGrid.isLoading.value)

  function openIncident(id: string) {
    activeIncidentId.value = id
    isDialogOpen.value = true
    lockedIncident.value = queueGrid.items.value.find((i: any) => i.id === id) ?? null
  }

  function closeDialog() {
    processingByFileId.value = {}
    linkedCrash.value = null
    relations.value = []
    isDialogOpen.value = false
    activeIncidentId.value = null
    lockedIncident.value = null
  }

  async function navigateNextAfterAction(indexBeforeAction: number) {
    lockedIncident.value = null
    await queueGrid.fetch()
    const items = queueGrid.items.value
    if (items.length === 0) {
      closeDialog()
      return
    }
    if (indexBeforeAction < 0) {
      closeDialog()
      return
    }
    const nextIndex = Math.min(indexBeforeAction, items.length - 1)
    const nextId = (items[nextIndex] as any).id
    activeIncidentId.value = nextId
    lockedIncident.value = items[nextIndex]
  }

  async function approveIncident(id: string) {
    const idx = activeIndex.value
    const ops: Operation[] = [
      { op: 'replace', path: '/moderationStatus', value: ModerationStatus.Approved }
    ]
    await adminIncidentReportsApi.adminIncidentReportsUpdateById({ id, operation: ops })
    await navigateNextAfterAction(idx)
  }

  async function rejectIncident(id: string) {
    const idx = activeIndex.value
    const ops: Operation[] = [
      { op: 'replace', path: '/moderationStatus', value: ModerationStatus.Rejected }
    ]
    await adminIncidentReportsApi.adminIncidentReportsUpdateById({ id, operation: ops })
    await navigateNextAfterAction(idx)
  }

  async function patchIncident(id: string, operations: Operation[]) {
    await adminIncidentReportsApi.adminIncidentReportsUpdateById({ id, operation: operations })
  }

  async function setPrimaryFile(incidentId: string, fileId: string) {
    const item = queueGrid.items.value.find((i: any) => i.id === incidentId) as any
    if (item) item.primaryUserIncidentFileId = fileId
    await patchIncident(incidentId, [
      { op: 'replace', path: '/primaryUserIncidentFileId', value: fileId }
    ])
  }

  async function getFootageForDownload(id: string) {
    return adminIncidentFootageApi.adminIncidentFootageGetForDownloadById({ id })
  }

  async function getImageForDownload(id: string) {
    return adminIncidentImagesApi.adminIncidentImagesGetForDownloadById({ id })
  }

  async function deleteFootage(id: string) {
    await adminIncidentFootageApi.adminIncidentFootageDeleteById({ id })
  }

  async function deleteImage(id: string) {
    await adminIncidentImagesApi.adminIncidentImagesDeleteById({ id })
  }

  async function fetchProcessing(reportId: string) {
    try {
      const files = await adminIncidentReportsApi.adminIncidentReportsGetProcessingById({
        id: reportId
      })
      // Ignore a late answer for an incident that is no longer open.
      if (activeIncidentId.value !== reportId) return
      processingByFileId.value = Object.fromEntries(files.map((f) => [f.fileId!, f]))
    } catch (e) {
      console.error(`Error fetching processing detail for incident ${reportId}:`, e)
    }
  }

  /** Loads the crash for the panel. Clears the old one first; a failure leaves it null, which the panel shows as "unavailable". */
  async function fetchLinkedCrash(reportId: string, crashId: string | null | undefined) {
    linkedCrash.value = null
    if (!crashId) return
    try {
      const crash = await crashesApi.crashesGetById({ id: crashId })
      // Ignore a late answer for an incident that is no longer open.
      if (activeIncidentId.value !== reportId) return
      linkedCrash.value = crash
    } catch (e) {
      console.error(`Error fetching linked crash ${crashId} for incident ${reportId}:`, e)
    }
  }

  /** Loads the relations for the panel. Clears the old ones first; a failure leaves none, so the panel is hidden. */
  async function fetchRelations(reportId: string) {
    relations.value = []
    try {
      const loaded = await adminIncidentReportsApi.adminIncidentReportsGetRelationsById({
        id: reportId
      })
      // Ignore a late answer for an incident that is no longer open.
      if (activeIncidentId.value !== reportId) return
      relations.value = loaded
    } catch (e) {
      console.error(`Error fetching relations for incident ${reportId}:`, e)
    }
  }

  /**
   * Sends staff's decisions, one PATCH per relation, then reloads them. Relations go before the report itself: an approved
   * relation on a report that is still pending stays hidden, so a failure half way never publishes anything.
   */
  async function reviewRelations(reviews: RelationReview[]) {
    if (reviews.length === 0) return
    const reportId = activeIncidentId.value
    for (const review of reviews) {
      await adminIncidentReportRelationsApi.adminIncidentReportRelationsUpdateById({
        id: review.id,
        operation: review.operations
      })
    }
    if (reportId && activeIncidentId.value === reportId) await fetchRelations(reportId)
  }

  /** Reloads the queue and the crash for the open incident, after the server refused a change because the incident moved on. */
  async function reloadActiveIncident() {
    const id = activeIncidentId.value
    await queueGrid.fetch()
    if (!id || activeIncidentId.value !== id) return
    await fetchLinkedCrash(id, activeIncident.value?.openDataCrashId)
    await fetchRelations(id)
  }

  async function reprocessUserFile(id: string) {
    await adminUserFilesApi.adminUserFilesReprocessFileById({ id })
  }

  return {
    queueGrid,
    queueTotal,
    queueItems,
    queueLoading,
    activeIncidentId,
    activeIncident,
    activeIndex,
    isDialogOpen,
    openIncident,
    closeDialog,
    navigateNextAfterAction,
    approveIncident,
    rejectIncident,
    patchIncident,
    setPrimaryFile,
    getFootageForDownload,
    getImageForDownload,
    deleteFootage,
    deleteImage,
    reprocessUserFile,
    processingByFileId,
    fetchProcessing,
    linkedCrash,
    fetchLinkedCrash,
    relations,
    fetchRelations,
    reviewRelations,
    reloadActiveIncident
  }
})
